<template>
  <div>
    <div v-if="roleName" :class="cls.roleRow">
      <Icon name="new_line_pointer_icon" :class="cls.roleLinePointer" />
      <Importance :importance="roleImportance" :type="false" />
      <AssignmentLink :class="cls.link" />
      <CommitmentIcon v-if="isDropdown" :type="commitmentIconType" :class="cls.commitIcon" />
      <div v-if="show_codes" :class="cls.roleText">{{ roleCode }}</div>
      <Icon name="org_icon_role" :class="cls.roleIcon" />
      <div :class="cls.roleTextWrapper">
        <span :class="cls.roleName">{{ roleName }}</span>
      </div>
    </div>

    <div v-if="positionName" :class="cls.positionRow">
      <Icon name="new_line_pointer_icon" :class="cls.positionLinePointer" />
      <Importance :importance="positionImportance" :type="false" />
      <AssignmentLink :class="cls.link" />
      <CommitmentIcon v-if="isDropdown" :type="commitmentIconType" :class="cls.commitIcon" />
      <div v-if="show_codes" :class="cls.positionText">{{ positionCode }}</div>
      <Icon name="org_icon_position" :class="cls.positionIcon" />
      <AsEmployeeCommitmentCard
        v-if="isCard"
        :commitment="positionCommitment"
        :labelShow="false"
        :class="cls.positionCommit"
      />
      <div :class="cls.positionTextWrapper">
        <span :class="cls.positionName">{{ positionName }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import Icon from "@components/icons/Icon.vue";
import AssignmentLink from "@components/AssignmentLink/AssignmentLink";
import Importance from "@components/importance/Importance.vue";
import CommitmentIcon from "@components/CommitmentIcon/CommitmentIcon.vue";
import AsEmployeeCommitmentCard from "@components/AsEmployeeCommitmentCard/AsEmployeeCommitmentCard";

const props = defineProps({
  roleCode: {
    type: String,
    default: "",
  },
  roleName: {
    type: String,
    default: "",
  },
  roleImportance: {
    type: Number,
    default: 0,
  },
  positionCode: {
    type: String,
    default: "",
  },
  positionName: {
    type: String,
    default: "",
  },
  positionImportance: {
    type: Number,
    default: 0,
  },
  positionCommitment: {
    type: String,
    default: "Primary",
  },
  show_codes: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    required: true,
    validator: (value) => ["card", "dropdown"].includes(value),
  },
});

const VARIANT_CLASSES = {
  card: {
    roleRow: "d-flex gap ms-4 role-item",
    positionRow: "d-flex gap ms-9 position-item",
    roleLinePointer: "line-pointer",
    positionLinePointer: "line-pointer1",
    link: "change-width",
    roleText: "role-text",
    positionText: "position-text",
    roleIcon: "roleIcon",
    positionIcon: "posIcon",
    roleTextWrapper: "role-text",
    roleName: "roleName",
    positionTextWrapper: "position-text",
    positionName: "posName",
    positionCommit: "pos-commit",
  },
  dropdown: {
    roleRow: "rad__row rad__row--role",
    positionRow: "rad__row rad__row--position",
    roleLinePointer: "rad__line-pointer",
    positionLinePointer: "rad__line-pointer rad__line-pointer--pos",
    link: "rad__link",
    commitIcon: "rad__commit-icon rad__row-commit",
    roleText: "rad__role-text",
    positionText: "rad__pos-text",
    roleIcon: "rad__role-icon",
    positionIcon: "rad__pos-icon",
    roleTextWrapper: "rad__role-text rad__role-text--truncate",
    roleName: "rad__role-name",
    positionTextWrapper: "rad__pos-text rad__pos-text--truncate",
    positionName: "rad__pos-name",
    positionCommit: "",
  },
};

const isDropdown = computed(() => props.variant === "dropdown");
const isCard = computed(() => props.variant === "card");

const cls = computed(() => VARIANT_CLASSES[props.variant] || VARIANT_CLASSES.card);

// Normalize positionCommitment to CommitmentIcon type (same variant as position assignment).
const commitmentIconType = computed(() => {
  const c = props.positionCommitment;
  if (!c) return "Primary";
  const s = String(c).toLowerCase();
  if (s.includes("acting")) return "Acting As";
  if (s.includes("back") || s.includes("backup")) return "Backup";
  if (s.includes("preparing")) return "Preparing For";
  return "Primary";
});
</script>

<style scoped>
/* ------------------------------------------------------------------ *
 * Self-contained layout for ResponsibilityAssignmentPathRows.
 * These rules previously lived in the parents' GLOBAL <style> blocks
 * (ResponsibilityAssignmentCard.vue under `.res-assignment-card`, and
 * ResponsibilityAssignmentDropDown.vue under `.rad-card`). They are
 * re-targeted here off the component's own class names so the rows
 * render correctly when mounted standalone (e.g. in Storybook).
 * ------------------------------------------------------------------ */

/* ------------------------- card variant ------------------------- */
.line-pointer {
  margin-left: -6px;
  margin-top: 8px;
}

.line-pointer1 {
  margin-left: -2px;
  margin-top: 8px;
}

.roleIcon {
  width: 16px;
}

.posIcon {
  width: 16px;
}

.change-width :deep(svg) {
  width: 16px;
  height: 16px;
  margin-top: 4px;
}

.pos-commit :deep(svg) {
  width: 18px;
  height: 18px;
  margin-top: 2px;
}

.gap {
  gap: 7px;
}

.role-text {
  font-family: Inter;
  font-size: 10px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: #475467;
  margin-bottom: 4px;
}

.role-text .roleName {
  font-weight: 500;
  color: #101828;
}

.position-text {
  font-family: Inter;
  font-size: 10px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: #475467;
}

.position-text .posName {
  font-weight: 500;
  color: #101828;
}

.role-item .importance-container {
  height: 18px !important;
  margin-top: 1px;
  margin-left: -2px;
}

.position-item .importance-container {
  height: 18px !important;
  margin-top: 1px;
  margin-left: -2px;
}

.role-item .importance-container :deep(.bar) {
  min-width: 5px !important;
  margin-left: 1px !important;
}

.position-item .importance-container :deep(.bar) {
  min-width: 5px !important;
  margin-left: 1px !important;
}

.as-employee-com {
  padding: 0px !important;
  margin-top: -6px;
  margin-left: -2px;
  margin-right: -2px;
}

.as-employee-com :deep(.employee-lbl-icon) {
  padding: 0px !important;
}

/* ------------------------ dropdown variant ------------------------ */
.rad__row {
  display: flex;
  gap: 5px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;
}

.rad__row.rad__row--role {
  padding-left: 12px;
}

.rad__row.rad__row--position {
  padding-left: 28px;
}

.rad__row:hover {
  background: #f9fafb;
}

.rad__line-pointer {
  margin-left: -2px;
  margin-top: 5px;
}

.rad__role-text {
  font-family: Inter;
  font-size: 10px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: #475467;
  margin-bottom: 4px;
}

.rad__role-text .rad__role-name {
  font-weight: 500;
  color: #101828;
}

.rad__pos-text {
  font-family: Inter;
  font-size: 10px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: #475467;
}

.rad__pos-text .rad__pos-name {
  font-weight: 500;
  color: #101828;
}

.rad__role-text--truncate,
.rad__pos-text--truncate {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.rad__role-icon {
  width: 16px;
}

.rad__pos-icon {
  width: 16px;
}

.rad__link :deep(svg) {
  width: 16px;
  height: 16px;
  margin-top: 4px;
}

.rad__commit-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.rad__commit-icon :deep(svg) {
  width: 18px;
  height: 18px;
}

.rad__row-commit {
  margin-top: 2px;
}

.rad__row--role .importance-container {
  height: 18px !important;
  margin-top: 1px;
  margin-left: -2px;
}

.rad__row--position .importance-container {
  height: 18px !important;
  margin-top: 1px;
  margin-left: -2px;
}

.rad__row--role .importance-container :deep(.bar) {
  min-width: 5px !important;
  margin-left: 1px !important;
}

.rad__row--position .importance-container :deep(.bar) {
  min-width: 5px !important;
  margin-left: 1px !important;
}
</style>
